import React from "react";
import { NextSeo } from "next-seo";
import Head from "next/head";
import Link from "next/link";
import { IoCheckmarkCircle } from "react-icons/io5";
import {
  FaTiktok,
  FaYoutube,
  FaInstagram,
  FaXTwitter,
  FaDiscord,
} from "react-icons/fa6";
import { ChevronRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/StaticAccordion";
import EmbeddedCreateWidget from "@/components/hub/EmbeddedCreateWidget";
import { JamVoiceCard, SectionHeading } from "@/components/home/JamCards";
import { HubConfig } from "@/config/hubPages";
import { hubAlternates } from "@/config/hubGroups";
import {
  LOCALE_META,
  translate,
  type Locale,
  type MessageKey,
} from "@jammable/ui-core/src/i18n";

const SITE_URL = "https://www.jammable.com";

// Badge string -> /ai-voices/[slug] (kept in sync with utils/categoryHubs).
const catSlug = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// Wraps the configured keyword in the lime highlight, as on the home hero.
function Headline({ h1, highlight }: { h1: string; highlight?: string }) {
  if (!highlight || !h1.includes(highlight)) return <>{h1}</>;
  const [before, after] = h1.split(highlight);
  return (
    <>
      {before}
      <span className="tw-whitespace-nowrap tw-rounded-[14px] tw-bg-jam-lime tw-px-2 tw-pb-0.5 md:tw-rounded-[22px] md:tw-px-3.5 md:tw-pb-1">
        {highlight}
      </span>
      {after}
    </>
  );
}

const PLATFORMS = [
  { key: "tt", bg: "#121212", icon: <FaTiktok size={15} /> },
  { key: "yt", bg: "#FF0033", icon: <FaYoutube size={16} /> },
  { key: "ig", bg: "#D62976", icon: <FaInstagram size={15} /> },
  { key: "dc", bg: "#5865F2", icon: <FaDiscord size={15} /> },
  { key: "x", bg: "#121212", icon: <FaXTwitter size={13} /> },
];

interface HeadTermHubProps {
  config: HubConfig;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialModels: any[];
}

const HeadTermHub = ({ config, initialModels }: HeadTermHubProps) => {
  // The page's copy and chrome are in the page's language, never the
  // visitor's: this is what Google indexes for this URL.
  const locale: Locale = config.locale ?? "en";
  const t = (k: MessageKey) => translate(locale, k);
  const path = config.path ?? `/${config.slug}`;
  const canonical = `${SITE_URL}${path === "/" ? "" : path}`;
  const homeUrl = locale === "en" ? SITE_URL : `${SITE_URL}/${locale}`;
  const altMap = hubAlternates(config.group) ?? config.alternates ?? {};
  const alternates = Object.entries(altMap) as [Locale, string][];
  const languageAlternates = alternates.length
    ? [
        ...alternates.map(([l, p]) => ({
          hrefLang: LOCALE_META[l].hreflang,
          href: `${SITE_URL}${p === "/" ? "" : p}`,
        })),
        // x-default: English where it exists, else the Spanish version.
        {
          hrefLang: "x-default",
          href: `${SITE_URL}${(altMap.en ?? altMap.es ?? path).replace(/^\/$/, "")}`,
        },
      ]
    : undefined;
  const models = Array.isArray(initialModels) ? initialModels : [];

  const softwareLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: config.appName,
    applicationCategory: "MultimediaApplication",
    operatingSystem: "Web",
    url: canonical,
    description: config.metaDescription,
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: config.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t("hub.home"), item: homeUrl },
      { "@type": "ListItem", position: 2, name: config.h1, item: canonical },
    ],
  };

  return (
    <>
      <NextSeo
        title={config.title}
        description={config.metaDescription}
        canonical={canonical}
        languageAlternates={languageAlternates}
        openGraph={{
          title: config.title,
          description: config.metaDescription,
          url: canonical,
          type: "website",
          locale: LOCALE_META[locale].og,
          siteName: "Jammable",
        }}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
        />
      </Head>
      <main className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1280px] tw-flex-col tw-gap-12 tw-bg-white tw-px-4 tw-pb-16 tw-pt-2 tw-text-jam-ink md:tw-gap-14 md:tw-px-8 md:tw-pt-4">
        {/* ── Hero ───────────────────────────────────────────────── */}
        <section className="tw-flex tw-flex-col tw-gap-8 tw-rounded-[34px] tw-bg-jam-lav tw-p-4 tw-pt-8 md:tw-rounded-[44px] md:tw-p-8 md:tw-pl-12 lg:tw-flex-row lg:tw-items-center lg:tw-gap-12">
          <div className="tw-flex tw-flex-1 tw-flex-col tw-items-start tw-gap-5 tw-px-1 md:tw-px-0">
            <div className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-full tw-bg-white tw-py-1.5 tw-pl-1.5 tw-pr-4">
              <div className="tw-flex">
                {PLATFORMS.map((p, i) => (
                  <span
                    key={p.key}
                    style={{ background: p.bg, marginLeft: i ? -8 : 0 }}
                    className="tw-flex tw-h-[32px] tw-w-[32px] tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-border-solid tw-border-white tw-text-white"
                  >
                    {p.icon}
                  </span>
                ))}
              </div>
              <span className="tw-text-xs tw-font-bold tw-text-jam-muted md:tw-text-sm">
                <span className="tw-font-extrabold tw-text-jam-ink">
                  {t("hub.views.count")}
                </span>{" "}
                {t("hub.views.on")}
              </span>
            </div>

            <h1 className="jam-display tw-m-0 tw-text-[40px] tw-font-extrabold tw-leading-[1.02] md:tw-text-[68px] md:tw-leading-none">
              <Headline h1={config.h1} highlight={config.highlight} />
            </h1>

            <p className="tw-m-0 tw-max-w-xl tw-text-[15px] tw-font-semibold tw-leading-relaxed tw-text-jam-muted md:tw-text-lg">
              {config.heroSub}
            </p>

            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {(
                config.badges ?? [
                  t("hub.badge.covers"),
                  t("hub.badge.quality"),
                  t("hub.badge.youtube"),
                ]
              ).map((t) => (
                <span
                  key={t}
                  className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-white tw-px-3.5 tw-py-2 tw-text-[13px] tw-font-extrabold tw-text-jam-ink"
                >
                  <IoCheckmarkCircle className="tw-text-jam-purple" size={17} />
                  {t}
                </span>
              ))}
            </div>
          </div>

          <EmbeddedCreateWidget popular={models} locale={locale} />
        </section>

        {/* ── Intro ──────────────────────────────────────────────── */}
        <p className="tw-mx-auto tw-my-0 tw-max-w-3xl tw-text-center tw-text-base tw-font-semibold tw-leading-relaxed tw-text-jam-muted md:tw-text-lg">
          {config.intro}
        </p>

        {config.features && (
          <section className="tw-flex tw-flex-col tw-gap-4">
            <SectionHeading title={config.features.heading} />
            <div className="tw-grid tw-grid-cols-1 tw-gap-2.5 sm:tw-grid-cols-2 md:tw-gap-3.5 lg:tw-grid-cols-3">
              {config.features.items.map((f) => (
                <div
                  key={f.title}
                  className="tw-flex tw-flex-col tw-gap-2 tw-rounded-[28px] tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-p-6"
                >
                  <h3 className="jam-display tw-m-0 tw-text-xl tw-font-extrabold">
                    {f.title}
                  </h3>
                  <p className="tw-m-0 tw-text-sm tw-font-semibold tw-leading-relaxed tw-text-jam-muted">
                    {f.body}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Popular voices ─────────────────────────────────────── */}
        {models.length > 0 && (
          <section className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-items-end tw-justify-between tw-gap-3">
              <div>
                <span className="tw-text-xs tw-font-extrabold tw-uppercase tw-tracking-[0.16em] tw-text-jam-purple">
                  {t("hub.trending")}
                </span>
                <h2 className="jam-display tw-m-0 tw-mt-1 tw-text-[26px] tw-font-extrabold md:tw-text-[32px]">
                  {t("hub.popular")}
                </h2>
              </div>
              <Link
                href="/models"
                className="tw-whitespace-nowrap tw-rounded-full tw-bg-jam-lav tw-px-4 tw-py-2.5 tw-text-sm tw-font-extrabold tw-text-jam-ink tw-no-underline hover:tw-text-jam-purple"
              >
                {t("hub.browseAll")} &rarr;
              </Link>
            </div>
            <div className="tw-grid tw-grid-cols-3 tw-gap-2.5 sm:tw-grid-cols-4 md:tw-grid-cols-6 md:tw-gap-3.5">
              {models.map((m) => (
                <JamVoiceCard key={m._id} model={m} />
              ))}
            </div>
          </section>
        )}

        {/* ── Categories ─────────────────────────────────────────── */}
        <section className="tw-flex tw-flex-col tw-gap-4">
          <SectionHeading title={t("hub.byCategory")} />
          <div className="tw-grid tw-grid-cols-2 tw-gap-2.5 sm:tw-grid-cols-3 md:tw-gap-3.5 lg:tw-grid-cols-6">
            {config.categories.map((cat) => (
              <Link
                key={cat.filter}
                href={`/ai-voices/${catSlug(cat.filter)}`}
                className="tw-flex tw-h-28 tw-flex-col tw-justify-between tw-rounded-[26px] tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-p-4 tw-text-jam-ink tw-no-underline tw-transition-colors hover:tw-border-jam-purple"
              >
                <ChevronRight
                  size={18}
                  strokeWidth={2.6}
                  className="tw-self-end tw-text-jam-purple"
                />
                <span>
                  <span className="jam-display tw-block tw-text-lg tw-font-extrabold tw-leading-tight">
                    {cat.label}
                  </span>
                  <span className="tw-text-xs tw-font-bold tw-text-jam-muted">
                    {t("hub.aiVoices")}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* ── How it works ───────────────────────────────────────── */}
        <section className="tw-flex tw-flex-col tw-gap-4">
          <SectionHeading title={config.stepsHeading ?? t("hub.howItWorks")} />
          <ol className="tw-m-0 tw-grid tw-list-none tw-grid-cols-1 tw-gap-2.5 tw-p-0 md:tw-grid-cols-3 md:tw-gap-3.5">
            {config.steps.map((step, i) => (
              <li
                key={step.title}
                className="tw-flex tw-flex-col tw-gap-2 tw-rounded-[28px] tw-bg-jam-lav tw-p-6"
              >
                <span className="jam-display tw-mb-2 tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime tw-text-lg tw-font-extrabold tw-text-jam-ink">
                  {i + 1}
                </span>
                <h3 className="jam-display tw-m-0 tw-text-xl tw-font-extrabold">
                  {step.title}
                </h3>
                <p className="tw-m-0 tw-text-sm tw-font-semibold tw-leading-relaxed tw-text-jam-muted">
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </section>

        {/* ── FAQ ────────────────────────────────────────────────── */}
        <section className="tw-flex tw-flex-col tw-gap-4">
          <SectionHeading title={t("hub.faq")} />
          <Accordion
            type="single"
            collapsible
            className="tw-w-full tw-rounded-[28px] tw-bg-jam-lav tw-px-5 md:tw-px-7"
          >
            {config.faqs.map((faq, i) => (
              <AccordionItem key={faq.q} value={`faq-${i}`}>
                <AccordionTrigger className="tw-text-left tw-text-base tw-font-extrabold tw-text-jam-ink">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="tw-text-sm tw-font-semibold tw-leading-relaxed tw-text-jam-muted">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* ── Keep exploring ─────────────────────────────────────── */}
        <section className="tw-flex tw-flex-col tw-gap-4 tw-rounded-[34px] tw-bg-jam-ink tw-p-6 tw-text-white md:tw-rounded-[44px] md:tw-p-10">
          <div>
            <h2 className="jam-display tw-m-0 tw-text-[26px] tw-font-extrabold md:tw-text-[32px]">
              {t("hub.keepExploring")}
            </h2>
            <p className="tw-m-0 tw-mt-1 tw-text-sm tw-font-semibold tw-text-jam-soft">
              {t("hub.moreWays")}
            </p>
          </div>
          <div className="tw-grid tw-grid-cols-1 tw-gap-2.5 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
            {config.related.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-[22px] tw-bg-jam-deep tw-px-[18px] tw-py-4 tw-text-[15px] tw-font-extrabold tw-text-white tw-no-underline hover:tw-text-jam-lime"
              >
                {link.label}
                <ChevronRight size={18} strokeWidth={2.6} />
              </Link>
            ))}
          </div>
        </section>
      </main>
    </>
  );
};

export default HeadTermHub;
